import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Check,
  X,
  MoreVertical,
  Lock,
  UserCheck,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { useFormik } from "formik";
import * as Yup from "yup";

// Import services
import authService from "@/services/authService";
// import { SentryTestButton } from "@/components/SentryTestButton";

// Types
type UserData = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  userType: "individual" | "company";
  companyName: string | null;
  siret: string | null;
};

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  companyName: string;
}

interface ProfileTabProps {
  user: UserData | null;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({ user }) => {
  const { t } = useTranslation();

  // Profile editing state
  const [isEditProfileOpen, setIsEditProfileOpen] = React.useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = React.useState(false);

  // Email change state
  const [isChangeEmailOpen, setIsChangeEmailOpen] = React.useState(false);
  const [emailChangeStep, setEmailChangeStep] = React.useState<"email" | "otp">(
    "email",
  );
  const [newEmail, setNewEmail] = React.useState("");
  const [emailOtp, setEmailOtp] = React.useState("");
  const [isRequestingEmailOtp, setIsRequestingEmailOtp] = React.useState(false);
  const [isVerifyingEmailOtp, setIsVerifyingEmailOtp] = React.useState(false);

  // Password change state
  const [isChangePasswordOpen, setIsChangePasswordOpen] = React.useState(false);
  const [passwordChangeStep, setPasswordChangeStep] = React.useState<
    "email" | "otp"
  >("email");
  const [passwordOtp, setPasswordOtp] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isRequestingPasswordOtp, setIsRequestingPasswordOtp] =
    React.useState(false);
  const [isVerifyingPasswordOtp, setIsVerifyingPasswordOtp] =
    React.useState(false);

  // Profile form validation schema
  const profileValidationSchema = Yup.object().shape({
    firstName: Yup.string().when("userType", {
      is: "individual",
      then: (schema) => schema.required(t("validation.firstNameRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
    lastName: Yup.string().when("userType", {
      is: "individual",
      then: (schema) => schema.required(t("validation.lastNameRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
    companyName: Yup.string().when("userType", {
      is: "company",
      then: (schema) => schema.required(t("validation.companyNameRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
    phone: Yup.string()
      .matches(
        /^(\+[0-9]{1,3})?[0-9]{9,10}$/,
        t("validation.invalidPhoneFormat"),
      )
      .nullable(),
    address: Yup.string().nullable(),
    city: Yup.string().nullable(),
    postalCode: Yup.string()
      .matches(/^[0-9]{5}$/, t("validation.invalidPostalCodeFormat"))
      .nullable(),
  });

  // Profile form with validation
  const profileForm = useFormik<ProfileFormData>({
    initialValues: {
      firstName: user?.userType === "individual" ? user?.firstName || "" : "",
      lastName: user?.userType === "individual" ? user?.lastName || "" : "",
      companyName: user?.userType === "company" ? user?.companyName || "" : "",
      phone: user?.phone || "",
      address: user?.address || "",
      city: user?.city || "",
      postalCode: user?.postalCode || "",
    },
    validationSchema: profileValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsUpdatingProfile(true);

        // Call the API to update the user profile
        // Include userType and company fields for company users
        const result = await authService.updateProfile({
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          address: values.address,
          city: values.city,
          postalCode: values.postalCode,
          userType: user?.userType,
          companyName: values.companyName,
          siret: user?.siret || undefined,
        });

        setIsEditProfileOpen(false);
        toast.success(
          result.message || t("dashboard.profile.profileUpdatedSuccess"),
        );

        // Refresh the page to show updated data
        window.location.reload();
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : t("dashboard.profile.profileUpdateError"),
        );
      } finally {
        setIsUpdatingProfile(false);
      }
    },
  });

  // Update profile form when user changes
  React.useEffect(() => {
    if (user) {
      profileForm.setValues({
        firstName: user.userType === "individual" ? user.firstName || "" : "",
        lastName: user.userType === "individual" ? user.lastName || "" : "",
        companyName: user.userType === "company" ? user.companyName || "" : "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        postalCode: user.postalCode || "",
      });
    }
  }, [user]);

  // Helper functions
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  // Email change handlers
  const handleRequestEmailChange = async () => {
    if (!newEmail || newEmail === user?.email) {
      toast.error(t("dashboard.profile.emailSameAsCurrent"));
      return;
    }

    try {
      setIsRequestingEmailOtp(true);
      const result = await authService.requestEmailChangeOTP(newEmail);

      toast.success(
        result.message || t("dashboard.profile.emailChangeOtpSent"),
      );
      setEmailChangeStep("otp");
    } catch (error) {
      console.error("Error requesting email change:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("dashboard.profile.emailChangeError"),
      );
    } finally {
      setIsRequestingEmailOtp(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    if (!emailOtp) {
      toast.error(t("dashboard.profile.otpRequired"));
      return;
    }

    try {
      setIsVerifyingEmailOtp(true);
      const result = await authService.verifyEmailChangeOTP(emailOtp);

      toast.success(
        result.message || t("dashboard.profile.emailChangedSuccess"),
      );
      setIsChangeEmailOpen(false);
      setEmailChangeStep("email");
      setNewEmail("");
      setEmailOtp("");

      // Refresh the page to show updated email
      window.location.reload();
    } catch (error) {
      console.error("Error verifying email change:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("dashboard.profile.emailVerifyError"),
      );
    } finally {
      setIsVerifyingEmailOtp(false);
    }
  };

  const handleCancelEmailChange = () => {
    setIsChangeEmailOpen(false);
    setEmailChangeStep("email");
    setNewEmail("");
    setEmailOtp("");
  };

  // Password change handlers
  const handleRequestPasswordChange = async () => {
    try {
      setIsRequestingPasswordOtp(true);
      const result = await authService.requestPasswordChangeOTP();

      toast.success(
        result.message || t("dashboard.profile.passwordResetOtpSent"),
      );
      setPasswordChangeStep("otp");
    } catch (error) {
      console.error("Error requesting password change OTP:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("dashboard.profile.passwordResetError"),
      );
    } finally {
      setIsRequestingPasswordOtp(false);
    }
  };

  const handleVerifyPasswordOtp = async () => {
    if (!passwordOtp) {
      toast.error(t("dashboard.profile.otpRequired"));
      return;
    }

    if (!newPassword || !confirmPassword) {
      toast.error(t("dashboard.profile.passwordFieldsRequired"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("dashboard.profile.passwordMismatch"));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t("dashboard.profile.passwordTooShort"));
      return;
    }

    try {
      setIsVerifyingPasswordOtp(true);
      const result = await authService.verifyPasswordChangeOTP({
        otpCode: passwordOtp,
        newPassword: newPassword,
      });

      toast.success(
        result.message || t("dashboard.profile.passwordChangedSuccess"),
      );
      setIsChangePasswordOpen(false);
      setPasswordChangeStep("email");
      setPasswordOtp("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Error verifying password change OTP:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("dashboard.profile.otpVerificationError"),
      );
    } finally {
      setIsVerifyingPasswordOtp(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangePasswordOpen(false);
    setPasswordChangeStep("email");
    setPasswordOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };
  return (
    <div className="gap-6 p-6">
      {/* Profile Information Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>{t("dashboard.profile.personalInformation")}</span>
            </CardTitle>
            <CardDescription>
              {t("dashboard.profile.managePersonalInfo")}
            </CardDescription>
          </div>
          {/* Profile Actions Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60" align="end">
              <div className="space-y-1">
                <h4 className="text-sm font-medium text-muted-foreground px-2 py-1">
                  {t("dashboard.profile.profileActions")}
                </h4>
                <Separator />
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setIsEditProfileOpen(true)}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    {t("dashboard.profile.editProfile")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setIsChangeEmailOpen(true)}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {t("dashboard.profile.changeEmail")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setIsChangePasswordOpen(true)}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {t("dashboard.profile.changePassword")}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
            <DialogContent className="sm:min-w-[425px] ">
              <DialogHeader>
                <DialogTitle>{t("dashboard.profile.editProfile")}</DialogTitle>
                <DialogDescription>
                  {t("dashboard.profile.updateProfileInfo")}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={profileForm.handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    {user?.userType === "individual" ? (
                      <>
                        <div className="grid gap-2">
                          <Label htmlFor="firstName">
                            {t("dashboard.profile.firstName")}
                          </Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={profileForm.values.firstName}
                            onChange={profileForm.handleChange}
                            onBlur={profileForm.handleBlur}
                            placeholder={t("dashboard.profile.enterFirstName")}
                          />
                          {profileForm.touched.firstName &&
                            profileForm.errors.firstName && (
                              <p className="text-sm text-destructive">
                                {profileForm.errors.firstName}
                              </p>
                            )}
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="lastName">
                            {t("dashboard.profile.lastName")}
                          </Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={profileForm.values.lastName}
                            onChange={profileForm.handleChange}
                            onBlur={profileForm.handleBlur}
                            placeholder={t("dashboard.profile.enterLastName")}
                          />
                          {profileForm.touched.lastName &&
                            profileForm.errors.lastName && (
                              <p className="text-sm text-destructive">
                                {profileForm.errors.lastName}
                              </p>
                            )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid gap-2">
                          <Label htmlFor="companyName">
                            {t("dashboard.profile.companyName")}
                          </Label>
                          <Input
                            id="companyName"
                            name="companyName"
                            value={profileForm.values.companyName}
                            onChange={profileForm.handleChange}
                            onBlur={profileForm.handleBlur}
                            placeholder={t(
                              "dashboard.profile.enterCompanyName",
                            )}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="phone">
                      {t("dashboard.profile.phone")}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={profileForm.values.phone}
                      onChange={profileForm.handleChange}
                      onBlur={profileForm.handleBlur}
                      placeholder={t("dashboard.profile.enterPhone")}
                    />
                    {profileForm.touched.phone && profileForm.errors.phone && (
                      <p className="text-sm text-destructive">
                        {profileForm.errors.phone}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="address">
                      {t("dashboard.profile.address")}
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={profileForm.values.address}
                      onChange={profileForm.handleChange}
                      onBlur={profileForm.handleBlur}
                      placeholder={t("dashboard.profile.enterAddress")}
                    />
                    {profileForm.touched.address &&
                      profileForm.errors.address && (
                        <p className="text-sm text-destructive">
                          {profileForm.errors.address}
                        </p>
                      )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="city">
                        {t("dashboard.profile.city")}
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        value={profileForm.values.city}
                        onChange={profileForm.handleChange}
                        onBlur={profileForm.handleBlur}
                        placeholder={t("dashboard.profile.enterCity")}
                      />
                      {profileForm.touched.city && profileForm.errors.city && (
                        <p className="text-sm text-destructive">
                          {profileForm.errors.city}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="postalCode">
                        {t("dashboard.profile.postalCode")}
                      </Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={profileForm.values.postalCode}
                        onChange={profileForm.handleChange}
                        onBlur={profileForm.handleBlur}
                        placeholder={t("dashboard.profile.enterPostalCode")}
                      />
                      {profileForm.touched.postalCode &&
                        profileForm.errors.postalCode && (
                          <p className="text-sm text-destructive">
                            {profileForm.errors.postalCode}
                          </p>
                        )}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <div className="flex flex-col md:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditProfileOpen(false)}
                      disabled={isUpdatingProfile}
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t("dashboard.profile.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdatingProfile || !profileForm.isValid}
                    >
                      {isUpdatingProfile ? (
                        <>
                          <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                          {t("dashboard.profile.updating")}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t("dashboard.profile.saveChanges")}
                        </>
                      )}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Profile Header */}
          <div className="flex items-center space-x-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src="" alt={user?.firstName} />
              <AvatarFallback className="text-xl font-semibold">
                {user?.userType === "individual"
                  ? getInitials(user?.firstName || "", user?.lastName || "")
                  : getInitials(user?.companyName || "", "")}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <h3
                className={`text-xl font-semibold ${user?.userType === "company" ? "mb-0" : ""}`}
              >
                {user?.userType === "individual"
                  ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
                  : user?.companyName || ""}
              </h3>
              {user?.userType === "company" && (
                <p className="text-xs text-muted-foreground">
                  SIRET: {user?.siret || ""}
                </p>
              )}
              <div className="flex items-center space-x-2">
                <Badge
                  variant={user?.isEmailVerified ? "default" : "secondary"}
                  className="text-xs"
                >
                  {user?.isEmailVerified ? (
                    <>
                      <Shield className="h-3 w-3 mr-1" />
                      {t("dashboard.profile.verified")}
                    </>
                  ) : (
                    t("dashboard.profile.unverified")
                  )}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {user?.subscriptionPlan || t("dashboard.profile.freeUser")}
                </Badge>
                {user?.userType === "company" ? (
                  <Badge variant="outline" className="text-xs">
                    {t("dashboard.profile.company")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    {t("dashboard.profile.individual")}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Separator />

          {/* Contact Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t("dashboard.profile.contactInformation")}
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{user?.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.profile.emailAddress")}
                    </p>
                  </div>
                </div>

                {/* Email Change Dialog */}
                <Dialog
                  open={isChangeEmailOpen}
                  onOpenChange={setIsChangeEmailOpen}
                >
                  <DialogTrigger asChild>
                    <div style={{ display: "none" }}></div>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>
                        {t("dashboard.profile.changeEmail")}
                      </DialogTitle>
                      <DialogDescription>
                        {emailChangeStep === "email"
                          ? t("dashboard.profile.enterNewEmail")
                          : t("dashboard.profile.enterOtpSent")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      {emailChangeStep === "email" ? (
                        <div className="grid gap-2">
                          <Label htmlFor="newEmail">
                            {t("dashboard.profile.newEmail")}
                          </Label>
                          <Input
                            id="newEmail"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={t("dashboard.profile.enterNewEmail")}
                          />
                          <p className="text-xs text-muted-foreground">
                            {t("dashboard.profile.currentEmail")}: {user?.email}
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-2">
                          <Label htmlFor="emailOtp">
                            {t("dashboard.profile.verificationCode")}
                          </Label>
                          <div className="flex justify-center">
                            <InputOTP
                              maxLength={6}
                              value={emailOtp}
                              onChange={setEmailOtp}
                            >
                              <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                              </InputOTPGroup>
                              <InputOTPSeparator />
                              <InputOTPGroup>
                                <InputOTPSlot index={3} />
                                <InputOTPSlot index={4} />
                                <InputOTPSlot index={5} />
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                          <p className="text-xs text-muted-foreground text-center">
                            {t("dashboard.profile.otpSentTo")}: {newEmail}
                          </p>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancelEmailChange}
                        disabled={isRequestingEmailOtp || isVerifyingEmailOtp}
                      >
                        <X className="h-4 w-4 mr-2" />
                        {t("dashboard.profile.cancel")}
                      </Button>
                      {emailChangeStep === "email" ? (
                        <Button
                          type="submit"
                          onClick={handleRequestEmailChange}
                          disabled={isRequestingEmailOtp || !newEmail}
                        >
                          {isRequestingEmailOtp ? (
                            <>
                              <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                              {t("dashboard.profile.sending")}
                            </>
                          ) : (
                            <>
                              <Mail className="h-4 w-4 mr-2" />
                              {t("dashboard.profile.sendOtp")}
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          onClick={handleVerifyEmailChange}
                          disabled={isVerifyingEmailOtp || !emailOtp}
                        >
                          {isVerifyingEmailOtp ? (
                            <>
                              <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                              {t("dashboard.profile.verifying")}
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              {t("dashboard.profile.verifyAndChange")}
                            </>
                          )}
                        </Button>
                      )}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Password Change Dialog */}
                <Dialog
                  open={isChangePasswordOpen}
                  onOpenChange={setIsChangePasswordOpen}
                >
                  <DialogTrigger asChild>
                    <div style={{ display: "none" }}></div>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>
                        {t("dashboard.profile.changePassword")}
                      </DialogTitle>
                      <DialogDescription>
                        {passwordChangeStep === "email"
                          ? t("dashboard.profile.passwordResetDescription")
                          : t("dashboard.profile.enterOtpSent")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      {passwordChangeStep === "email" ? (
                        <div className="grid gap-2">
                          <p className="text-sm text-muted-foreground">
                            {t("dashboard.profile.passwordResetEmailInfo")}:{" "}
                            {user?.email}
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          <div className="grid gap-2">
                            <Label htmlFor="passwordOtp">
                              {t("dashboard.profile.verificationCode")}
                            </Label>
                            <div className="flex justify-center">
                              <InputOTP
                                maxLength={6}
                                value={passwordOtp}
                                onChange={setPasswordOtp}
                              >
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                </InputOTPGroup>
                                <InputOTPSeparator />
                                <InputOTPGroup>
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </div>
                            <p className="text-xs text-muted-foreground text-center">
                              {t("dashboard.profile.otpSentTo")}: {user?.email}
                            </p>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="newPassword">
                              {t("dashboard.profile.newPassword")}
                            </Label>
                            <Input
                              id="newPassword"
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder={t(
                                "dashboard.profile.enterNewPassword",
                              )}
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="confirmPassword">
                              {t("dashboard.profile.confirmPassword")}
                            </Label>
                            <Input
                              id="confirmPassword"
                              type="password"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              placeholder={t(
                                "dashboard.profile.confirmNewPassword",
                              )}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t("dashboard.profile.passwordRequirements")}
                          </p>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <div className="flex flex-col md:flex-row gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelPasswordChange}
                          disabled={
                            isRequestingPasswordOtp || isVerifyingPasswordOtp
                          }
                        >
                          <X className="h-4 w-4 mr-2" />
                          {t("dashboard.profile.cancel")}
                        </Button>
                        {passwordChangeStep === "email" ? (
                          <Button
                            type="submit"
                            onClick={handleRequestPasswordChange}
                            disabled={isRequestingPasswordOtp}
                          >
                            {isRequestingPasswordOtp ? (
                              <>
                                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                                {t("dashboard.profile.sending")}
                              </>
                            ) : (
                              <>
                                <Mail className="h-4 w-4 mr-2" />
                                {t("dashboard.profile.sendPasswordResetOtp")}
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            onClick={handleVerifyPasswordOtp}
                            disabled={
                              isVerifyingPasswordOtp ||
                              !passwordOtp ||
                              !newPassword ||
                              !confirmPassword
                            }
                          >
                            {isVerifyingPasswordOtp ? (
                              <>
                                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                                {t("dashboard.profile.verifying")}
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-2" />
                                {t("dashboard.profile.verifyAndChange")}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {user?.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{user.phone}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.profile.phoneNumber")}
                    </p>
                  </div>
                </div>
              )}
              {(user?.address || user?.city) && (
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {[user?.address, user?.city, user?.postalCode]
                        .filter(Boolean)
                        .join(", ") || t("dashboard.profile.noAddress")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.profile.address")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sentry Test Panel - À retirer en production */}
      {/* <div className="mt-6">
        <SentryTestButton />
      </div> */}
    </div>
  );
};
